import React, { useState, useEffect } from 'react';
import { Cloud, Sun, CloudRain, CloudSnow, CloudLightning, CloudDrizzle, Wind, Droplets, Thermometer, CloudFog, Snowflake } from 'lucide-react';
import { format, parseISO, isSameDay } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { getDateLocale } from '../utils/dateLocale';
import api from '../services/api';

const getWeatherInfo = (code) => {
  if (code === null || code === undefined) return { icon: Cloud, label: 'weather.unknown', color: 'text-slate-400' };
  if (code === 0) return { icon: Sun, label: 'weather.clear', color: 'text-amber-500' };
  if (code <= 3) return { icon: Cloud, label: 'weather.cloudy', color: 'text-slate-400' };
  if (code <= 49) return { icon: CloudFog, label: 'weather.fog', color: 'text-slate-500' };
  if (code <= 59) return { icon: CloudDrizzle, label: 'weather.drizzle', color: 'text-blue-400' };
  if (code <= 69) return { icon: CloudRain, label: 'weather.rain', color: 'text-blue-500' };
  if (code <= 79) return { icon: Snowflake, label: 'weather.snow', color: 'text-cyan-400' };
  if (code <= 84) return { icon: CloudRain, label: 'weather.showers', color: 'text-blue-600' };
  if (code <= 86) return { icon: CloudSnow, label: 'weather.snowShowers', color: 'text-cyan-500' };
  if (code <= 99) return { icon: CloudLightning, label: 'weather.thunderstorm', color: 'text-purple-500' };
  return { icon: Cloud, label: 'weather.unknown', color: 'text-slate-400' };
};

const WeatherWidget = ({ latitude, longitude, eventDate }) => {
  const { t } = useTranslation();
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!latitude || !longitude) { setLoading(false); return; }
    const fetchWeather = async () => {
      try {
        setLoading(true);
        const dateParam = eventDate ? format(new Date(eventDate), 'yyyy-MM-dd') : '';
        const res = await api.get(`/weather?lat=${latitude}&lng=${longitude}&event_date=${dateParam}`);
        setWeather(res.data);
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    fetchWeather();
  }, [latitude, longitude, eventDate]);

  if (!latitude || !longitude) return null;
  if (loading) return (
    <div className="bg-gradient-to-br from-slate-800 to-slate-900 p-6 animate-pulse" data-testid="weather-loading">
      <div className="h-4 bg-slate-700 rounded w-32 mb-4" />
      <div className="flex gap-3">
        {[...Array(7)].map((_, i) => <div key={i} className="h-24 w-16 bg-slate-700 rounded" />)}
      </div>
    </div>
  );
  if (error || !weather) return null;

  const { forecast, event_day } = weather;
  const today = new Date();
  const eventDateObj = eventDate ? new Date(eventDate) : null;

  return (
    <div className="bg-gradient-to-br from-slate-800 to-slate-900 p-6 overflow-hidden" data-testid="weather-widget">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2">
          <Thermometer className="w-5 h-5 text-brand" />
          <h3 className="font-heading font-bold text-white uppercase tracking-wider text-sm">
            {t('weather.title')}
          </h3>
        </div>
        <span className="text-[10px] text-slate-500 font-heading uppercase tracking-widest">Open-Meteo</span>
      </div>

      {/* 7-day forecast */}
      <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-thin" data-testid="weather-forecast">
        {forecast.map((day) => {
          const dateObj = parseISO(day.date);
          const isToday = isSameDay(dateObj, today);
          const isEventDay = eventDateObj && isSameDay(dateObj, eventDateObj);
          const info = getWeatherInfo(day.weather_code);
          const WeatherIcon = info.icon;

          return (
            <div
              key={day.date}
              className={`flex-shrink-0 w-[88px] rounded-lg p-3 text-center transition-all ${
                isEventDay
                  ? 'bg-brand/20 border border-brand ring-1 ring-brand/30'
                  : isToday
                  ? 'bg-slate-700/60 border border-slate-600'
                  : 'bg-slate-700/30 border border-transparent hover:bg-slate-700/50'
              }`}
              data-testid={`weather-day-${day.date}`}
            >
              {/* Day label */}
              <div className="text-[10px] font-heading uppercase tracking-widest text-slate-400 mb-1">
                {isToday ? t('weather.today') : format(dateObj, 'EEE', { locale: getDateLocale() })}
              </div>
              <div className="text-[10px] text-slate-500 mb-2">
                {format(dateObj, 'd MMM', { locale: getDateLocale() })}
              </div>

              {/* Icon */}
              <WeatherIcon className={`w-7 h-7 mx-auto mb-2 ${info.color}`} />

              {/* Temp */}
              <div className="flex items-center justify-center gap-1 mb-1">
                <span className="text-white font-bold text-sm">{Math.round(day.temp_max)}°</span>
                <span className="text-slate-500 text-xs">{Math.round(day.temp_min)}°</span>
              </div>

              {/* Details */}
              <div className="flex items-center justify-center gap-2 text-[9px] text-slate-500">
                {day.precipitation > 0 && (
                  <span className="flex items-center gap-0.5">
                    <Droplets className="w-2.5 h-2.5 text-blue-400" />
                    {day.precipitation}mm
                  </span>
                )}
                {day.wind_speed > 20 && (
                  <span className="flex items-center gap-0.5">
                    <Wind className="w-2.5 h-2.5" />
                    {Math.round(day.wind_speed)}
                  </span>
                )}
              </div>

              {/* Badge */}
              {isEventDay && (
                <div className="mt-1.5 bg-brand text-white text-[8px] font-heading font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-sm">
                  {t('weather.eventDay')}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Event day special section */}
      {event_day && (
        <div className="mt-4 bg-brand/10 border border-brand/20 rounded-lg p-4" data-testid="weather-event-day">
          <div className="flex items-center gap-3">
            {(() => {
              const info = getWeatherInfo(event_day.weather_code);
              const Icon = info.icon;
              return <Icon className={`w-10 h-10 ${info.color}`} />;
            })()}
            <div className="flex-1">
              <div className="text-xs text-brand font-heading font-bold uppercase tracking-widest mb-0.5">
                {t('weather.eventForecast')}
              </div>
              <div className="text-white text-sm">
                {t(getWeatherInfo(event_day.weather_code).label)} — {Math.round(event_day.temp_min)}° / {Math.round(event_day.temp_max)}°
              </div>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-1 text-xs text-slate-400">
                <Wind className="w-3 h-3" /> {Math.round(event_day.wind_speed)} km/h
              </div>
              <div className="flex items-center gap-1 text-xs text-slate-400 mt-1">
                <Droplets className="w-3 h-3 text-blue-400" /> {event_day.precipitation} mm
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Message if event is too far */}
      {eventDateObj && !event_day && (
        <div className="mt-4 text-center text-xs text-slate-500 py-2">
          {t('weather.forecastUnavailable')}
        </div>
      )}
    </div>
  );
};

export default WeatherWidget;
